import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, MousePointer2, Eraser, Trash2, Loader2,
  AlertCircle, CheckCircle2, Home, ListTree, RefreshCw,
  Link2, Info, Droplet, Sun, ArrowUpRight, Pentagon, Boxes,
} from 'lucide-react';
import { Button } from '../../ui/button';
import StructureCanvas from './StructureCanvas';
import { PhaseSnapshotButton } from './PhaseSnapshotButton';
import {
  usePlanEditor, ROOM_TYPES, RAIL_STYLES, roomStyleFor,
} from './PlanEditorContext';
import { QuickNameChips, QuickNamingBanner } from './RoomQuickNaming';
import { ZoneFloorPicker, useZoneFloors } from './ZoneFloorPicker';
import { isGenericRoomLabel, openZoneRooms } from '../../../lib/roomNaming';
import { LaunchPanel } from './LaunchPanel';
import MaisonVisualizers from './MaisonVisualizers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TabPieces — V14.49 Sprint 4 (Onglet 3 · Les Pièces).
 *
 * Workflow :
 *   1. Click "Détecter les Pièces" → Gemini returns rooms (label, type, polygon).
 *   2. Each room renders as a semi-transparent polygon on the canvas, tinted
 *      by sec/humide classification (humide → cyan/blue, sec → orange/violet).
 *   3. Click a room on the canvas or in the right panel to select.
 *   4. Change the type via the dropdown → the polygon recolors instantly AND
 *      the backend creates the matching Matériauthèque slots (idempotent).
 *   5. Auto-saved (debounced 600ms) just like Sprint 2/3.
 */
const TabPieces = ({ projectId, selectedLot, selectedLotId, savedState, isMaison = false, onOpenProduction }) => {
  const isMultiLot = !!selectedLot || !!selectedLotId;
  const blockingReason = useMemo(() => {
    if (isMultiLot && !selectedLotId) return 'Sélectionnez un lot dans la barre latérale.';
    if (!savedState?.source_file_id) return 'Uploadez un plan dans l\u2019étape 1 · Plan source.';
    return null;
  }, [isMultiLot, selectedLotId, savedState]);

  const bgImageUrl = useMemo(() => {
    if (!savedState?.source_file_id) return null;
    const fid = savedState?.cleaned_file_id || savedState.source_file_id;
    const endpoint = savedState?.cleaned_file_id ? 'render' : 'source';
    const lotQ = selectedLotId ? `&lot_id=${selectedLotId}` : '';
    return `${API}/projects/${projectId}/plan-source/${endpoint}?file_id=${fid}${lotQ}`;
  }, [projectId, savedState, selectedLotId]);

  // Force activeSubTab → 'rooms' while this tab is mounted so the canvas
  // wires rooms as the interactive layer (walls/openings remain visible
  // but read-only).
  const { setActiveSubTab, setMode } = usePlanEditor();
  useEffect(() => {
    setActiveSubTab('rooms');
    setMode('select');
  }, [setActiveSubTab, setMode]);

  return (
    <div className="grid grid-cols-12 gap-3 h-full min-h-0" data-testid="tab-pieces-root">
      <div className="col-span-8 min-h-0 flex flex-col rounded-lg border overflow-hidden"
           style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'var(--c-border)' }}>
        <RoomsToolbar projectId={projectId} selectedLotId={selectedLotId}
                      disabled={!!blockingReason}
                      cleanedReady={!!savedState?.cleaned_file_id} />
        <div className="flex-1 min-h-0 relative">
          {blockingReason ? (
            <Blocker text={blockingReason} />
          ) : (
            <StructureCanvas bgImageUrl={bgImageUrl} />
          )}
        </div>
        <RoomsLegend />
      </div>

      <div className="col-span-4 min-h-0 flex flex-col rounded-lg border"
           style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}>
        <RoomsPanel projectId={projectId} lotId={selectedLotId} disabled={!!blockingReason} />
        <LaunchPanel projectId={projectId} lotId={selectedLotId} disabled={!!blockingReason}
                     onOpenProduction={onOpenProduction} />
      </div>

      {isMaison && (
        <div className="col-span-12 min-h-0">
          <MaisonVisualizers projectId={projectId} />
        </div>
      )}

      <RoomsKeyboardShortcuts disabled={!!blockingReason} />
    </div>
  );
};

// =====================================================================
// ROOMS TOOLBAR
// =====================================================================

const RoomsToolbar = ({ projectId, selectedLotId, disabled, cleanedReady }) => {
  const { mode, setMode, rooms, actions, saving, isDirty, lastRoomsSyncReport } = usePlanEditor();
  const [detecting, setDetecting] = useState(false);
  const [detectingTopo, setDetectingTopo] = useState(false);
  const [lastDetection, setLastDetection] = useState(null);

  const handleDetect = useCallback(async () => {
    if (disabled) return;
    setDetecting(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/detect-rooms${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      const r = await axios.post(url, null, { withCredentials: true });
      const det = r.data?.detection;
      if (!det || !Array.isArray(det.rooms) || det.rooms.length === 0) {
        toast.warning('Aucune pièce détectée — vous pouvez en typer manuellement.');
        setLastDetection({ count: 0, confidence: det?.confidence || 0 });
        return;
      }
      actions.replaceRoomsFromDetection(det.rooms);
      setLastDetection({ count: det.rooms.length, confidence: det.confidence || 0 });
      toast.success(`${det.rooms.length} pièce(s) détectée(s) · confiance ${Math.round((det.confidence || 0) * 100)}%`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) toast.error(detail || 'Patientez quelques secondes avant une nouvelle détection.');
      else toast.error(detail || 'Détection IA indisponible');
    } finally {
      setDetecting(false);
    }
  }, [projectId, selectedLotId, actions, disabled]);

  const handleClear = useCallback(() => {
    if (rooms.length === 0) return;
    if (!window.confirm(`Supprimer les ${rooms.length} pièces en cours d\u2019édition ?`)) return;
    actions.clearAllRooms();
  }, [rooms.length, actions]);

  // V14.62 Sprint 17 LOT 5 — Topological room detection.
  // Computes the planar subdivision of walls+outer into closed faces
  // and proposes each as a candidate room. Replaces the existing list
  // after user confirmation (so a single wall split → 2 new rooms).
  const handleDetectTopo = useCallback(async () => {
    if (disabled) return;
    if (rooms.length > 0 && !window.confirm(
        `Détection des zones : les ${rooms.length} pièce(s) actuelle(s) seront remplacées par les zones fermées calculées depuis vos murs (épaisseur réelle, jonctions tolérées). Continuer ?`)) {
      return;
    }
    setDetectingTopo(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/structure/detect-rooms-from-topology${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      const r = await axios.post(url, { replace_existing: true }, { withCredentials: true });
      const detected = r.data?.rooms || [];
      actions.replaceRoomsFromDetection(detected, { keepUserRooms: false, source: 'topology' });
      if (detected.length === 0) {
        toast.warning('Aucune zone fermée trouvée — vérifiez que vos murs se rejoignent (normalisez les tracés).');
      } else {
        toast.success(`${detected.length} zone(s) fermée(s) détectée(s) — renommez, fusionnez ou supprimez.`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Détection topologique impossible');
    } finally {
      setDetectingTopo(false);
    }
  }, [projectId, selectedLotId, disabled, rooms.length, actions]);

  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b flex-wrap"
         style={{ borderColor: 'var(--c-border)', background: 'rgba(255,255,255,0.02)' }}
         data-testid="rooms-toolbar">
      {/* V15.33 — détection géométrique (zones fermées par les murs) = voie principale, sans IA */}
      <Button onClick={handleDetectTopo} disabled={disabled || detectingTopo} size="sm"
              className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-[11px]"
              data-testid="detect-rooms-topology-btn"
              title="Repère les zones fermées par vos murs (épaisseur réelle, tolérance de jonction) — instantané, 0 crédit. Puis renommez, fusionnez ou supprimez.">
        {detectingTopo ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Boxes className="w-3.5 h-3.5 mr-1.5" />}
        {detectingTopo ? 'Calcul…' : 'Détecter les zones (murs)'}
      </Button>
      <Button onClick={handleDetect} disabled={disabled || detecting} size="sm"
              variant="outline"
              className="h-7 border-white/15 text-gray-300 hover:bg-white/5 text-[11px]"
              data-testid="detect-rooms-btn"
              title="Lecture du plan par IA (lent, interprétatif) — à réserver aux plans sans murs tracés">
        {detecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
        {detecting ? 'Détection…' : 'IA (optionnel)'}
      </Button>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolBtn icon={MousePointer2} label="Sélectionner" hint="S" value="select" mode={mode}
               onClick={setMode} disabled={disabled} testId="rooms-tool-select" />
      <ToolBtn icon={Pentagon} label="Dessiner" hint="D" value="draw-room-polygon" mode={mode}
               onClick={setMode} disabled={disabled} testId="rooms-tool-draw" />
      <ToolBtn icon={Eraser} label="Gomme" hint="E" value="erase" mode={mode}
               onClick={setMode} disabled={disabled} testId="rooms-tool-erase" />
      <button type="button" onClick={handleClear}
              disabled={disabled || rooms.length === 0}
              className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-rose-300 border border-rose-400/30 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="rooms-clear-all" title="Effacer toutes les pièces">
        <Trash2 className="w-3 h-3" />
        Tout effacer
      </button>
      <div className="ml-auto flex items-center gap-2 text-[10.5px]">
        {lastRoomsSyncReport?.created?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-emerald-300"
                title="Matériauthèque mise à jour" data-testid="rooms-materialtheque-sync-flash">
            <Link2 className="w-3 h-3" /> +{lastRoomsSyncReport.created.length} slot(s) Matériauthèque
          </span>
        )}
        {cleanedReady && (
          <span className="inline-flex items-center gap-1 text-teal-300"
                title="Le canvas utilise la version nettoyée du plan">
            <CheckCircle2 className="w-3 h-3" /> Plan nettoyé
          </span>
        )}
        {lastDetection && (
          <span className="text-gray-400" data-testid="rooms-detection-meta">
            IA · {lastDetection.count} pièces · {Math.round((lastDetection.confidence || 0) * 100)}%
          </span>
        )}
        <SaveIndicator saving={saving} isDirty={isDirty} />
        <PhaseSnapshotButton projectId={projectId} selectedLotId={selectedLotId}
                             phase="pieces" disabled={disabled} />
      </div>
    </div>
  );
};

const ToolBtn = ({ icon: Icon, label, hint, value, mode, onClick, disabled, testId }) => {
  const active = mode === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      disabled={disabled}
      title={hint ? `${label} (${hint})` : label}
      className={`inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active
        ? 'bg-amber-500/15 text-amber-200 border-amber-400/40'
        : 'border-white/10 text-gray-300 hover:bg-white/5'}`}
      data-testid={testId}
    >
      <Icon className="w-3 h-3" />
      {label}
      {hint && <kbd className="ml-0.5 px-1 text-[9px] rounded bg-white/5 border border-white/10 text-gray-400">{hint}</kbd>}
    </button>
  );
};

const SaveIndicator = ({ saving, isDirty }) => {
  if (saving) return <span className="text-amber-300 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Sauvegarde…</span>;
  if (isDirty) return <span className="text-amber-300/80 inline-flex items-center gap-1" data-testid="rooms-dirty"><RefreshCw className="w-3 h-3" /> Modifié</span>;
  return <span className="text-teal-300/80 inline-flex items-center gap-1" data-testid="rooms-saved"><CheckCircle2 className="w-3 h-3" /> Enregistré</span>;
};

const Blocker = ({ text }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-[12px] text-gray-400">
    <AlertCircle className="w-6 h-6 text-amber-400" />
    <div className="max-w-[32ch]">{text}</div>
  </div>
);

const RoomsLegend = () => (
  <div className="px-2.5 py-1 border-t flex items-center gap-3 text-[9.5px] text-gray-500"
       style={{ borderColor: 'var(--c-border)' }}>
    <span className="inline-flex items-center gap-1">
      <Sun className="w-3 h-3 text-amber-400" /> Pièce sèche
    </span>
    <span className="inline-flex items-center gap-1">
      <Droplet className="w-3 h-3 text-sky-400" /> Pièce humide (cuisine, SDB, WC)
    </span>
    <span className="ml-auto inline-flex items-center gap-1.5">
      <kbd className="px-1 rounded bg-white/5 border border-white/10">S</kbd> Sélection ·
      <kbd className="px-1 rounded bg-white/5 border border-white/10">D</kbd> Dessiner polygone ·
      <kbd className="px-1 rounded bg-white/5 border border-white/10">E</kbd> Gomme ·
      <kbd className="px-1 rounded bg-white/5 border border-white/10">Esc</kbd> Annuler ·
      <kbd className="px-1 rounded bg-white/5 border border-white/10">Suppr</kbd> Supprimer
    </span>
  </div>
);

// =====================================================================
// ROOMS PANEL — right column
// =====================================================================

const RoomsPanel = ({ projectId, lotId, disabled }) => {
  const {
    rooms, walls, selectedRoomId, setSelectedRoomId,
    hoveredRoomId, setHoveredRoomId, actions, lastRoomsSyncReport,
  } = usePlanEditor();

  // V15.33 — fusion de deux zones (serveur : pontage de la cloison, polygone unique).
  const handleMerge = useCallback(async (keepId, otherId) => {
    try {
      const url = `${API}/projects/${projectId}/plan-source/structure/rooms/merge${lotId ? `?lot_id=${lotId}` : ''}`;
      const r = await axios.post(url, { room_ids: [keepId, otherId] }, { withCredentials: true });
      actions.setRoomsFromServer(r.data?.rooms || []);
      toast.success('Zones fusionnées');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fusion impossible');
    }
  }, [projectId, lotId, actions]);

  // V14.97 — pièces en zone ouverte (mur invisible) → sol dédié en Matériauthèque.
  const openZones = useMemo(() => openZoneRooms(walls, rooms), [walls, rooms]);
  // V14.98 — sols des zones ouvertes renseignés depuis l'onglet (options Matériauthèque + slots)
  const zoneFloors = useZoneFloors(projectId, lotId, openZones.size > 0);
  const zoneSyncKey = JSON.stringify((lastRoomsSyncReport?.zones || []).map((z) => z.room_id));
  useEffect(() => { if (openZones.size > 0) zoneFloors.refresh(); }, [zoneSyncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const g = { wet: [], dry: [] };
    rooms.forEach((r) => {
      const cfg = roomStyleFor(r.type);
      (cfg.wet ? g.wet : g.dry).push(r);
    });
    return g;
  }, [rooms]);

  const aiCount = rooms.filter((r) => r.source === 'ai').length;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="rooms-panel">
      <div className="p-2.5 border-b" style={{ borderColor: 'var(--c-border)' }}>
        <h4 className="text-[12px] font-semibold flex items-center gap-1.5"
            style={{ color: 'var(--c-gold)' }}>
          <ListTree className="w-3.5 h-3.5" /> Pièces détectées
        </h4>
        <div className="text-[10.5px] text-gray-400 mt-0.5">
          {rooms.length} pièce(s) · IA {aiCount} · Sèche {grouped.dry.length} · Humide {grouped.wet.length}
        </div>
        {lastRoomsSyncReport?.created?.length > 0 && (
          <div className="mt-2 text-[10px] text-emerald-300/90 bg-emerald-500/10 border border-emerald-400/20 rounded px-1.5 py-1 flex items-start gap-1.5"
               data-testid="rooms-materialtheque-sync-badge">
            <Link2 className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              <strong>{lastRoomsSyncReport.created.length}</strong> slot(s) ajoutés à la Matériauthèque (
              {lastRoomsSyncReport.created.map((c) => c.name).slice(0, 3).join(', ')}
              {lastRoomsSyncReport.created.length > 3 ? '…' : ''}). Assignez-leur un matériau pour
              préciser le rendu 3D.
            </span>
          </div>
        )}
        {/* V14.96 — Nommage rapide des pièces (un clic = nom + type) */}
        {!disabled && <QuickNamingBanner />}
        {/* V14.97 — Sols par zone ouverte */}
        {!disabled && openZones.size > 0 && (
          <div className="mt-2 text-[10px] rounded px-1.5 py-1 flex items-start gap-1.5"
               style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', color: '#86efac' }}
               data-testid="rooms-open-zones-note">
            <Link2 className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              <strong>{openZones.size}</strong> pièce(s) en zone ouverte → un <strong>slot de sol dédié</strong> chacune dans la
              Matériauthèque (« Sol · Cuisine (zone ouverte) »…) : carrelage côté cuisine, parquet côté séjour.
              {projectId && (
                <> <a href={`/hub/materials/${projectId}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-white" data-testid="rooms-open-zones-link">Renseigner →</a></>
              )}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {disabled ? (
          <p className="text-[10.5px] text-gray-500 italic">Liste indisponible — étapes précédentes incomplètes.</p>
        ) : rooms.length === 0 ? (
          <EmptyHint />
        ) : (
          [
            { key: 'wet',  label: 'Pièces humides', icon: Droplet, color: '#0ea5e9' },
            { key: 'dry',  label: 'Pièces sèches',  icon: Sun,     color: '#fbbf24' },
          ].map((group) => {
            const items = grouped[group.key];
            if (items.length === 0) return null;
            const Icon = group.icon;
            return (
              <div key={group.key} className="mb-3">
                <div className="flex items-center gap-1.5 text-[10.5px] font-semibold mb-1"
                     style={{ color: group.color }}>
                  <Icon className="w-3 h-3" />
                  {group.label}
                  <span className="text-gray-500 font-normal">({items.length})</span>
                </div>
                <ul className="space-y-1">
                  {items.map((r, idx) => (
                    <RoomRow
                      key={r.id}
                      room={r}
                      index={idx + 1}
                      projectId={projectId}
                      isSelected={r.id === selectedRoomId}
                      isHovered={r.id === hoveredRoomId}
                      onHoverEnter={() => setHoveredRoomId(r.id)}
                      onHoverLeave={() => setHoveredRoomId(null)}
                      onSelect={() => setSelectedRoomId(r.id)}
                      onPatch={(patch) => actions.updateRoom(r.id, patch)}
                      onDelete={() => actions.deleteRoom(r.id)}
                      otherRooms={rooms.filter((o) => o.id !== r.id)}
                      onMerge={(otherId) => handleMerge(r.id, otherId)}
                      openNeighbours={openZones.get(r.id)}
                      zoneSlot={zoneFloors.slots.find((z) => z.room_id === r.id) || (lastRoomsSyncReport?.zones || []).find((z) => z.room_id === r.id)}
                      zoneFloorOptions={zoneFloors.options}
                      lotId={lotId}
                      onZoneFloorSaved={(slot) => zoneFloors.patchSlot(slot)}
                    />
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const EmptyHint = () => (
  <div className="text-center text-[10.5px] text-gray-500 p-4 border border-dashed rounded"
       style={{ borderColor: 'var(--c-border)' }}>
    <Home className="w-5 h-5 mx-auto mb-1.5 opacity-50" />
    Aucune pièce. Cliquez sur <span className="text-amber-300">Détecter les zones (murs)</span>
    : chaque zone fermée par vos murs devient une pièce à renommer.
    <div className="mt-1.5 text-[9.5px] inline-flex items-center gap-1">
      <Info className="w-3 h-3" />
      Astuce : des murs qui se rejoignent à moins de 12 cm sont considérés fermés.
    </div>
  </div>
);

const RoomRow = ({ room, index, projectId, isSelected, isHovered,
                   onHoverEnter, onHoverLeave, onSelect, onPatch, onDelete,
                   openNeighbours, zoneSlot, zoneFloorOptions, lotId, onZoneFloorSaved,
                   otherRooms = [], onMerge }) => {
  const style = roomStyleFor(room.type);
  const firstSubrole = style.subroles?.[0];
  const materialthequeHref = projectId && firstSubrole
    ? `/hub/materials/${projectId}?subrole=${encodeURIComponent(firstSubrole)}`
    : null;

  return (
    <li
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onClick={onSelect}
      className={`group rounded cursor-pointer text-[10.5px] border transition-colors px-1.5 py-1 ${isSelected
        ? 'bg-amber-500/15 border-amber-400/40'
        : isHovered ? 'bg-white/10 border-white/20'
                    : 'border-transparent hover:bg-white/5'}`}
      data-testid={`room-row-${room.id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-1.5 rounded-sm shrink-0" style={{ background: style.color }} />
        <span className="font-mono text-gray-400 w-5 shrink-0">#{index}</span>
        <input
          value={room.label || ''}
          onChange={(e) => { e.stopPropagation(); onPatch({ label: e.target.value }); }}
          onClick={(e) => e.stopPropagation()}
          placeholder={style.label}
          className="bg-transparent text-gray-200 flex-1 px-1 py-0.5 text-[10.5px] border border-transparent focus:border-white/20 rounded outline-none"
          data-testid={`room-label-${room.id}`}
        />
        <select
          value={room.type}
          onChange={(e) => { e.stopPropagation(); onPatch({ type: e.target.value }); }}
          onClick={(e) => e.stopPropagation()}
          className="bg-transparent border rounded px-1 py-0.5 text-[10px]"
          style={{ borderColor: 'var(--c-border)' }}
          data-testid={`room-type-${room.id}`}
        >
          {ROOM_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}{t.wet ? ' 💧' : ''}</option>
          ))}
        </select>
        {materialthequeHref && (
          <a
            href={materialthequeHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-emerald-400 opacity-60 group-hover:opacity-100 hover:text-emerald-200 inline-flex items-center"
            title={`Voir les matériaux liés (${style.subroles?.join(', ')})`}
            data-testid={`room-view-material-${room.id}`}
          >
            <ArrowUpRight className="w-3 h-3" />
          </a>
        )}
        <button type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="text-rose-400 opacity-60 group-hover:opacity-100 hover:text-rose-200"
                title="Supprimer cette pièce"
                data-testid={`room-delete-${room.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {/* V15.18 — extérieur : bordure / hauteur / couvert (défauts du type, modifiables) */}
      {style.outdoor && (() => {
        const d = style.ext || {};
        const railStyle = room.rail_style || d.rail_style || 'barreaude';
        const railH = room.rail_height_m ?? d.rail_height_m ?? 1.0;
        const covered = room.covered ?? d.covered ?? false;
        return (
          <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[9.5px]" data-testid={`room-ext-params-${room.id}`}
               onClick={(e) => e.stopPropagation()}>
            <select value={railStyle} onChange={(e) => onPatch({ rail_style: e.target.value })}
                    className="bg-transparent border rounded px-1 py-0.5 text-[9.5px]" style={{ borderColor: 'var(--c-border)' }}
                    title="Bordure rendue dans la maquette blanche" data-testid={`room-rail-style-${room.id}`}>
              {RAIL_STYLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {railStyle !== 'aucun' && (
              <label className="inline-flex items-center gap-1 text-gray-400">
                h
                <input type="number" step="0.05" min="0.1" max="2" value={railH}
                       onChange={(e) => onPatch({ rail_height_m: parseFloat(e.target.value) || 0 })}
                       className="w-12 bg-transparent border rounded px-1 py-0.5 text-[9.5px] text-gray-200" style={{ borderColor: 'var(--c-border)' }}
                       data-testid={`room-rail-height-${room.id}`} /> m
              </label>
            )}
            <label className="inline-flex items-center gap-1 text-gray-400" title="Couvert : sous-face de dalle rendue au-dessus (loggia)">
              <input type="checkbox" checked={!!covered} onChange={(e) => onPatch({ covered: e.target.checked })}
                     className="accent-lime-500" data-testid={`room-covered-${room.id}`} /> couvert
            </label>
          </div>
        );
      })()}
      {/* V14.97 — zone ouverte : sol dédié */}
      {openNeighbours?.length > 0 && (
        <div className="mt-1 flex items-center gap-1 flex-wrap text-[9.5px]" data-testid={`room-open-zone-${room.id}`}>
          <span className="px-1 rounded" style={{ background: 'rgba(34,197,94,0.12)', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)' }}>
            Zone ouverte ↔ {openNeighbours.join(', ')}
          </span>
          {!zoneSlot?.filled && <span className="text-amber-300/90">sol dédié à renseigner ↓</span>}
        </div>
      )}
      {openNeighbours?.length > 0 && (
        <ZoneFloorPicker projectId={projectId} lotId={lotId} room={room} options={zoneFloorOptions || []}
                         slot={zoneSlot} onSaved={onZoneFloorSaved} />
      )}
      {/* V14.96 — suggestions en un clic pour les pièces sans nom (ou sélectionnées) */}
      {(isSelected || isGenericRoomLabel(room.label)) && (
        <div className="mt-1.5">
          <QuickNameChips room={room} compact={!isSelected} onPick={(s) => onPatch({ label: s.label, type: s.type })} />
        </div>
      )}
      {/* V15.33 — fusion de zones : la cloison entre les deux est pontée, le nom/type de celle-ci est conservé */}
      {isSelected && onMerge && otherRooms.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[9.5px]" onClick={(e) => e.stopPropagation()}>
          <span className="text-gray-500 inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> Fusionner avec</span>
          <select defaultValue="" onChange={(e) => { if (e.target.value) onMerge(e.target.value); e.target.value = ''; }}
                  className="h-5 text-[9.5px] rounded px-1 bg-white/[0.06] border border-white/10 text-white flex-1"
                  data-testid={`room-merge-select-${room.id}`}>
            <option value="">— choisir une zone —</option>
            {otherRooms.map((o) => <option key={o.id} value={o.id}>{o.label || o.type || o.id}</option>)}
          </select>
        </div>
      )}
      {isSelected && style.subroles?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1 text-[9.5px]">
          <span className="text-gray-500">Subroles Matériauthèque :</span>
          {style.subroles.map((sr) => (
            <span key={sr} className="px-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-400/20">
              {sr}
            </span>
          ))}
        </div>
      )}
    </li>
  );
};

// =====================================================================
// KEYBOARD SHORTCUTS — rooms tab
// =====================================================================

const RoomsKeyboardShortcuts = ({ disabled }) => {
  const { setMode, selectedRoomId, actions, polygonDraft } = usePlanEditor();
  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'select', 'textarea'].includes(tag)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 's') { setMode('select'); actions.cancelPolygonDraft(); e.preventDefault(); }
      else if (k === 'e') { setMode('erase'); actions.cancelPolygonDraft(); e.preventDefault(); }
      else if (k === 'd') { setMode('draw-room-polygon'); e.preventDefault(); }
      else if (e.key === 'Escape' && polygonDraft.length > 0) {
        actions.cancelPolygonDraft(); e.preventDefault();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedRoomId) {
        actions.deleteRoom(selectedRoomId); e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, setMode, selectedRoomId, actions, polygonDraft.length]);
  return null;
};

export default TabPieces;
